/**
 * Resolve ProfitLoss Client / LastYear for a PL code under a specific YOA Tab.
 * Used when seeding empty WP rows in MULTI — must not reuse first-tab client.
 */
import {useFsAdjustStore} from '@/store/audit';
import {findNodeByCodes} from '@/utils/fsCodes';

export type PlYoaClientAmounts = {
    clientValue: number;
    lastYearAudited: number;
};

/**
 * Resolve Client for code@yoa from periods[].plLine / amountsByYearOfAssessment / getPerData.
 */
export function resolvePlClientAmountsForYoa(
    code: string | null | undefined,
    yearOfAssessment?: string | null,
): PlYoaClientAmounts | null {
    const codeKey = String(code || '').trim().toUpperCase();
    if (!codeKey) return null;
    const yoa = String(yearOfAssessment || '').trim();
    const store = useFsAdjustStore.getState();

    const fromPeriodPlLine = (): PlYoaClientAmounts | null => {
        if (!yoa) return null;
        const periods = store.adjustRecord?.periods;
        if (!Array.isArray(periods) || periods.length === 0) return null;
        const hit = periods.find(
            (p) => String(p?.yearOfAssessment || '').trim() === yoa
                && Array.isArray(p?.plLine)
                && (p.plLine as unknown[]).length > 0,
        );
        if (!hit?.plLine) return null;
        const node = findNodeByCodes(hit.plLine as any[], [codeKey]);
        if (!node) return null;
        const byYoa = (node as any).amountsByYearOfAssessment as
            | Record<string, number | string>
            | null
            | undefined;
        let client = Number(
            (node as any).openingAmount ?? (node as any).client ?? (node as any).amount ?? 0,
        ) || 0;
        if (byYoa && typeof byYoa === 'object' && Object.keys(byYoa).length > 0) {
            const raw = byYoa[yoa];
            if (raw !== undefined && raw !== null && raw !== '') {
                client = Number(raw) || 0;
            } else {
                client = 0;
            }
        }
        return {
            clientValue: client,
            lastYearAudited: Number((node as any).lastYearAudited ?? 0) || 0,
        };
    };

    const fromAmountsMap = (): PlYoaClientAmounts | null => {
        if (!yoa) return null;
        const line = store.getLineByCode(codeKey) || store.getLineByCode(String(code || '').trim());
        if (!line) return null;
        const byYoa = (line as any).amountsByYearOfAssessment as
            | Record<string, number | string>
            | null
            | undefined;
        if (!byYoa || typeof byYoa !== 'object' || Object.keys(byYoa).length === 0) return null;
        const raw = byYoa[yoa];
        if (raw === undefined || raw === null || raw === '') {
            return {
                clientValue: 0,
                lastYearAudited: Number((line as any).lastYearAudited ?? 0) || 0,
            };
        }
        return {
            clientValue: Number(raw) || 0,
            lastYearAudited: Number((line as any).lastYearAudited ?? 0) || 0,
        };
    };

    const fromPeriod = fromPeriodPlLine();
    if (fromPeriod) return fromPeriod;

    const fromMap = fromAmountsMap();
    if (fromMap) return fromMap;

    const per = store.getPerData(codeKey, yoa || null);
    if (per) {
        return {
            clientValue: Number(per.clientValue ?? 0) || 0,
            lastYearAudited: Number(per.lastYearAudited ?? 0) || 0,
        };
    }

    const line = store.getLineByCode(codeKey) || store.getLineByCode(String(code || '').trim());
    if (!line) return null;
    return {
        clientValue: Number(
            (line as any).openingAmount ?? (line as any).amount ?? (line as any).finalAmount ?? 0,
        ) || 0,
        lastYearAudited: Number((line as any).lastYearAudited ?? 0) || 0,
    };
}

/** Overlay YOA client onto line so initialDataFactory reads the correct Tab amounts. */
export function withPlYoaClientOnLine<T extends Record<string, any> | null | undefined>(
    line: T,
    code: string | null | undefined,
    yearOfAssessment?: string | null,
): T {
    if (!line || typeof line !== 'object') return line;
    const amounts = resolvePlClientAmountsForYoa(code, yearOfAssessment);
    if (!amounts) return line;
    return {
        ...line,
        finalAmount: amounts.clientValue,
        amount: amounts.clientValue,
        openingAmount: amounts.clientValue,
        lastYearAudited: amounts.lastYearAudited,
    } as T;
}
